import {
  AnalyticsChartResponse,
  BillingByServiceCategoryResponse,
  InpatientDischargeDiagnosesResponse,
  PatientCategoryBillingResponse,
} from '../types';

/**
 * SHER-956. Recognising the analytics responses that answer with a `stats` block.
 *
 * ChartFactory receives whatever its endpoint answered, as a union of everything that can arrive
 * there. Several endpoints answer with a `stats` block, and none of those responses used to be in
 * that union — so every `originalData.stats` read in that file was a TS2339. An error is not a
 * check: those reads were verified by nothing, free to drift from what the API returns, and the
 * next chart of that shape got written by copying a line that already did not type.
 *
 * These are type predicates rather than accessors on purpose. A reader that returns the value has
 * to assert its way out, which checks nothing and reintroduces the same gap one layer down; a
 * predicate narrows the union, so the `.stats.diagnoses` read at the call site is checked against
 * the declared response, and a response missing from the union stops compiling here.
 *
 * They answer false when the payload does not carry what it claims, which makes "the period had no
 * discharges" and "the endpoint answered something we cannot read" distinguishable. Both call sites
 * in ChartFactory currently render the same empty table for either, so today that distinction is
 * available rather than used — worth knowing before someone reads a blank card as "no data".
 */

// `typeof`/`!== null` is type hygiene rather than a guard: both predicates below re-check what they
// find, so letting a null through would produce the same answer. It is here so this expression is
// true about what it returns, and it is deliberately not pinned by a test — a mutation removing it
// changes no observable behaviour, which is what "not a guard" means.
const statsBlockOf = (response: AnalyticsChartResponse): Record<string, unknown> | undefined => {
  if (typeof response !== 'object' || response === null || !('stats' in response)) return undefined;
  // The one cast in this module, and it narrows to "some object", not to a contract: several
  // members of the union declare `stats` as `any`, which would otherwise collapse the property
  // union and hand both predicates below an unchecked value.
  const stats = (response as { stats?: unknown }).stats;
  return typeof stats === 'object' && stats !== null ? (stats as Record<string, unknown>) : undefined;
};

/** `GET /inpatients/discharge-diagnoses/...` (SHER-583). */
export const isDischargeDiagnosesResponse = (
  response: AnalyticsChartResponse,
): response is InpatientDischargeDiagnosesResponse =>
  Array.isArray(statsBlockOf(response)?.diagnoses);

/** `GET /finances/category-billing-trends/...` */
export const isBillingByServiceCategoryResponse = (
  response: AnalyticsChartResponse,
): response is BillingByServiceCategoryResponse => {
  const categoryTotals = statsBlockOf(response)?.categoryTotals;
  return typeof categoryTotals === 'object' && categoryTotals !== null;
};

/**
 * `GET /finances/patient-category-billing/...` (SHER-957).
 *
 * This is the read SHER-956 left as an `as any` on purpose, because typing it then would have
 * meant writing down a shape the endpoint does not answer with.
 *
 * The two finance `stats` blocks are disjoint: this one carries `lines` and no `categoryTotals`,
 * its neighbour carries `categoryTotals` and no `lines`. (`categoryTotals` does appear on this
 * response — inside each line, never on the block.) So either key discriminates, and the two
 * predicates are already mutually exclusive; `lines` is the one checked here because it is also
 * the only part of the block this chart reads.
 */
export const isPatientCategoryBillingResponse = (
  response: AnalyticsChartResponse,
): response is PatientCategoryBillingResponse =>
  Array.isArray(statsBlockOf(response)?.lines);
